import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import {
  Tablecell,
  findBrokerAbb,
  calculateAndFormatNoAbs,
  findStrategy,
  twoDecimal,
  fourDecimal,
  TABLE_HEAD_FUNDALLOCATOR,
} from "../../../component/Methods";
import { GetAllocation } from "../../../component/Methods/dashboardCalculations";
import { useRebalancerState } from "../../../hooks/useOptimizedState";

const RebalancePopup = ({
  rebalanceType,
  hideDeltaData,
  deltaRebalancer,
  open,
  onClose,
  fundStrategyName,
  summaryData,
  compositeKey,
  fundId,
  strategyId,
}) => {
  const theme = useTheme();
  const {
    marketData,
    livePrices,
    markets,
    accountingData,
    managers,
    brokers,
    lots,
    funds,
    strategies,
  } = useRebalancerState();

  const [selectedStrategyAllocation, setSelectedStrategyAllocation] =
    useState(null);
  const [fundAum, setFundAum] = useState(0);

  useEffect(() => {
    if (open && fundId && strategyId && managers && accountingData) {
      const getStrategyAUMFromAccounting = () => {
        if (fundId && strategyId && accountingData?.[0]?.funds) {
          const foundFund = accountingData[0].funds.find((row) => {
            const fundIdValue =
              typeof row?.fund === "string" ? row?.fund : row?.fund?.fundId;
            return fundIdValue === fundId;
          });

          if (foundFund) {
            setFundAum(foundFund?.aum);
            return foundFund?.strategyAUMs?.[strategyId] || 0;
          }
        }
        return 0;
      };

      const getFund = () => {
        const fundData = funds?.find((f) => f.fundId === fundId);

        if (fundData?.allocation) {
          return {
            items: Object.fromEntries(
              Object.entries(fundData.allocation.items).map(([key, value]) => [
                key,
                {
                  ...value,
                  broker: findBrokerAbb(value?.broker, brokers),
                  percentLong: (value?.percentLong ?? value?.percentlong) * 100,
                  percentShort:
                    (value?.percentShort ?? value?.percentshort) * 100,
                  totalAllocation:
                    (value?.totalAllocation ?? value?.totalallocation) * 100,
                  ignoreWhenRebalancing:
                    value?.ignoreWhenRebalancing ??
                    value?.ignorewhenrebalancing,
                  byPass: value?.byPass ?? value?.bypass,
                },
              ])
            ),
          };
        }
        return null;
      };

      const getSelectedStrategyAllocation = () => {
        const allocation = getFund();

        if (allocation && allocation.items && strategyId) {
          const strategyAllocation = allocation.items[strategyId];

          if (strategyAllocation) {
            const strategy = findStrategy(strategyId, strategies);
            const strategyAUMValue = getStrategyAUMFromAccounting() || 0;
            const fundData = managers?.[0]?.funds?.[fundId];
            const activeAum = fundData?.activeAum || fundAum;
            const { long, short } = GetAllocation(
              fundId,
              strategy,
              lots,
              accountingData,
              funds,
              strategies,
              livePrices,
              markets
            );
            const existingPercent = calculateAndFormatNoAbs(
              (strategyAUMValue / fundAum) * 100
            );
            const fundAumCal = parseFloat(activeAum) || 0;
            const alcTarget =
              (fundAumCal * Number(strategyAllocation.totalAllocation)) / 100;
            const alcExisting = strategyAUMValue;

            const formattedLong = twoDecimal((long / strategyAUMValue) * 100);
            const formattedShort = twoDecimal(
              (Math.abs(short) / strategyAUMValue) * 100
            );
            const enhancedAllocation = {
              ...strategyAllocation,
              byPass: Boolean(strategyAllocation.byPass),
              ignoreWhenRebalancing: Boolean(
                strategyAllocation.ignoreWhenRebalancing
              ),

              existingAllocation: alcExisting
                ? calculateAndFormatNoAbs(alcExisting)
                : calculateAndFormatNoAbs(0),
              allocation: alcTarget ? calculateAndFormatNoAbs(alcTarget) : "",
              totalAllocation: strategyAllocation.totalAllocation
                ? fourDecimal(Number(strategyAllocation.totalAllocation))
                : "",
              percentShort: strategyAllocation.percentShort
                ? fourDecimal(
                    -Math.abs(Number(strategyAllocation.percentShort))
                  )
                : "",
              percentLong: strategyAllocation.percentLong
                ? fourDecimal(Number(strategyAllocation.percentLong))
                : "",
              existingPercent: Number.isFinite(Number(existingPercent))
                ? Number(existingPercent)
                : 0,
              long: Number.isFinite(Number(formattedLong))
                ? Number(formattedLong)
                : 0,
              short: Number.isFinite(Number(formattedShort))
                ? Number(formattedShort)
                : 0,
              broker: strategyAllocation.broker,
              account: strategyAllocation.account,
              strategy: strategy,
              fundAum: fundAum,
              activeAum: activeAum,
              strategyAUM: strategyAUMValue,
            };
            setSelectedStrategyAllocation(enhancedAllocation);
          } else {
            setSelectedStrategyAllocation(null);
          }
        } else {
          setSelectedStrategyAllocation(null);
        }
      };

      getSelectedStrategyAllocation();
    }
  }, [
    open,
    fundId,
    fundAum,
    strategyId,
    managers,
    accountingData,
    brokers,
    strategies,
    lots,
    funds,
    livePrices,
    markets,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xll"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "1000px",
          maxHeight: "90vh",
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            fontSize: "20px",
            color: theme.palette.text.primary,
          }}
        >
          {fundStrategyName}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            height: "25px",
            width: "25px",
            color: theme.palette.text.secondary,
          }}
        >
          <CloseIcon sx={{ height: "20px", width: "20px" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: "20px",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: "16px",
              marginBottom: "15px",
              color: theme.palette.primary.main,
            }}
          >
            Summary Overview
          </Typography>
          <Table
            size="small"
            sx={{
              borderCollapse: "collapse",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <TableBody>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.primary,
                }}
              >
                <TableCell
                  align="left"
                  colSpan={2}
                  sx={{
                    fontSize: "16px",
                    border: "none",
                    paddingLeft: "16px",
                    fontWeight: "900",
                    paddingBottom: "12px",
                    paddingTop: "12px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  Summary
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontSize: "16px",
                    border: "none",
                    fontWeight: "900",
                    paddingBottom: "12px",
                    paddingTop: "12px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  Existing
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontSize: "16px",
                    border: "none",
                    fontWeight: "900",
                    paddingBottom: "12px",
                    paddingTop: "12px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  New
                </TableCell>
                {rebalanceType === "specific" &&
                hideDeltaData !== true &&
                deltaRebalancer !== 0 ? (
                  <Tablecell
                    align="center"
                    sx={{
                      fontSize: "12px",
                      border: "none",
                      fontWeight: "bold",
                      paddingBottom: "10px",
                      color: theme.palette.text.primary,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      Preview
                      <Tooltip
                        title="Calculated Portfolio if delta rebalancer is executed"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "16px",
                              backgroundColor: "rgba(0, 0, 0, 0.87)",
                              padding: "8px 12px",
                              maxWidth: "300px",
                              lineHeight: "1.4",
                              color: theme.palette.text.primary,
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: "16px",
                            color: "grey.500",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </Tablecell>
                ) : null}
              </TableRow>

              {summaryData
                .filter((item) => item.compositeKey === compositeKey)
                .map((row) =>
                  row.summary.map((subRow, index) => (
                    <TableRow
                      key={subRow.name}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? theme.palette.grey[50]
                            : theme.palette.background.paper,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell
                        colSpan={2}
                        align="left"
                        sx={{
                          fontSize: "13px",
                          paddingBottom: "8px",
                          paddingTop: "8px",
                          paddingLeft: "16px",
                          border: "none",
                          fontWeight: "500",
                          color: theme.palette.text.primary,
                          backgroundColor:
                            index % 2 === 0
                              ? theme.palette.grey[50]
                              : theme.palette.background.paper,
                        }}
                      >
                        {subRow.name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "13px",
                          border: "none",
                          paddingBottom: "8px",
                          paddingTop: "8px",
                          fontWeight: "500",
                          color: theme.palette.text.primary,
                          backgroundColor:
                            index % 2 === 0
                              ? theme.palette.grey[50]
                              : theme.palette.background.paper,
                        }}
                      >
                        {subRow.existing}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "13px",
                          border: "none",
                          paddingBottom: "8px",
                          paddingTop: "8px",
                          fontWeight: "500",
                          color: theme.palette.text.primary,
                          backgroundColor:
                            index % 2 === 0
                              ? theme.palette.grey[50]
                              : theme.palette.background.paper,
                        }}
                      >
                        {subRow.new}
                      </TableCell>
                      {rebalanceType === "specific" &&
                      !hideDeltaData &&
                      deltaRebalancer !== 0 ? (
                        <Tablecell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            border: "none",
                            paddingBottom: "2px",
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        >
                          {subRow.delta}
                        </Tablecell>
                      ) : null}
                    </TableRow>
                  ))
                )}
            </TableBody>
          </Table>
        </Box>

        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: "16px",
              marginBottom: "15px",
              color: theme.palette.primary.main,
            }}
          >
            Comprehensive Allocation Data
          </Typography>
          <Table
            size="small"
            sx={{
              borderCollapse: "collapse",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <TableBody>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
              >
                <TableCell
                  align="center"
                  colSpan={5}
                  sx={{
                    fontSize: theme.typography.bodyText12.fontSize,
                    fontWeight: 900,
                    backgroundColor: theme.palette.primary.main,
                    padding: 0,
                    paddingLeft: "20px",
                    textAlign: "center",
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  Existing
                </TableCell>
                <TableCell
                  align="center"
                  colSpan={5}
                  sx={{
                    fontSize: theme.typography.bodyText12.fontSize,
                    fontWeight: 900,
                    backgroundColor: theme.palette.primary.main,
                    padding: 0,
                    paddingLeft: "20px",
                    textAlign: "center",
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  Target
                </TableCell>
                <TableCell
                  align="center"
                  colSpan={3}
                  sx={{
                    fontSize: theme.typography.bodyText12.fontSize,
                    fontWeight: 900,
                    backgroundColor: theme.palette.primary.main,
                    padding: 0,
                    paddingLeft: "20px",
                    textAlign: "center",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                    },
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      sx={{
                        fontSize: theme.typography.bodyText12.fontSize,
                        fontWeight: "900 !important",
                        marginRight: "8px",
                        fontFamily: "clashGrotesk !important",
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      Preferences{" "}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                {TABLE_HEAD_FUNDALLOCATOR.map((header) => (
                  <TableCell
                    key={header.id}
                    align={header.alignLeft ? "left" : "center"}
                    sx={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      paddingBottom: "10px",
                      paddingTop: "8px",
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
              {selectedStrategyAllocation ? (
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  {TABLE_HEAD_FUNDALLOCATOR.map((header) => (
                    <TableCell
                      key={header.id}
                      align={header.alignLeft ? "left" : "center"}
                      sx={{
                        fontSize: "12px",
                        border: "none",
                        paddingBottom: "8px",
                        paddingTop: "8px",
                        fontWeight: "500",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.grey[50],
                      }}
                    >
                      {header.id === "name"
                        ? selectedStrategyAllocation.strategy || "N/A"
                        : header.id === "ignoreWhenRebalancing"
                        ? selectedStrategyAllocation[header.id]
                          ? "TRUE"
                          : "FALSE"
                        : header.id === "byPass"
                        ? selectedStrategyAllocation[header.id]
                          ? "TRUE"
                          : "FALSE"
                        : typeof selectedStrategyAllocation[header.id] ===
                          "number"
                        ? selectedStrategyAllocation[header.id].toFixed(4)
                        : selectedStrategyAllocation[header.id] || "N/A"}
                    </TableCell>
                  ))}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_HEAD_FUNDALLOCATOR.length}
                    align="center"
                    sx={{
                      fontSize: "12px",
                      border: "none",
                      paddingBottom: "2px",
                      color: theme.palette.error.main,
                      fontStyle: "italic",
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    No allocation data found for this strategy
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RebalancePopup;
